import { KEYBOARD_OPEN_ATTRIBUTE, TEXT_FIELD_FOCUSED } from "@/shared/theme/keyboard";

/** A viewport this much shorter than when no field was focused means the on-screen keyboard is up. */
const KEYBOARD_MIN_HEIGHT = 150;

/** How long after a field gains focus the on-screen keyboard has to resize the viewport. */
const KEYBOARD_APPEAR_MS = 600;

/**
 * Sets `data-keyboard-open` on <html> while the on-screen keyboard is up, for `hideWhileTyping`.
 *
 * A focused field isn't enough: the Android back gesture closes the keyboard but leaves the field focused,
 * and a hardware keyboard (or a narrow desktop window) never opens one. So:
 * - On touch screens the bars hide as soon as a text field gains focus, before the viewport resizes, so they
 *   never jump up. If the viewport hasn't shrunk within KEYBOARD_APPEAR_MS, no keyboard appeared and they
 *   return.
 * - Whenever the viewport shrinks by a keyboard's height while a field is focused, they hide; when it grows
 *   back with the field still focused (the back gesture), they return. Tapping the field again reopens the
 *   keyboard and hides them once more.
 *
 * Returns a function that stops tracking.
 */
export function trackKeyboard(win: Window = window, doc: Document = document): () => void {
	const root = doc.documentElement;
	const viewport = win.visualViewport;
	const viewportHeight = () => viewport?.height ?? win.innerHeight;
	const textFieldFocused = () => doc.activeElement?.matches(TEXT_FIELD_FOCUSED) ?? false;
	const touchScreen = () => win.matchMedia?.("(pointer: coarse)").matches ?? false;
	const setOpen = (open: boolean) =>
		open ? root.setAttribute(KEYBOARD_OPEN_ATTRIBUTE, "") : root.removeAttribute(KEYBOARD_OPEN_ATTRIBUTE);

	/** Viewport height while no keyboard is up: the reference for "the viewport shrank". */
	let fullHeight = viewportHeight();
	let sawKeyboard = false;
	const viewportShrunk = () => viewportHeight() < fullHeight - KEYBOARD_MIN_HEIGHT;
	let appearTimer: ReturnType<typeof setTimeout> | undefined;

	const onFocusIn = () => {
		if (!textFieldFocused() || !touchScreen()) return;
		// Moving to the next field keeps the keyboard up, so it counts as already seen.
		sawKeyboard = viewportShrunk();
		setOpen(true);
		clearTimeout(appearTimer);
		appearTimer = setTimeout(() => {
			if (!sawKeyboard) setOpen(false);
		}, KEYBOARD_APPEAR_MS);
	};

	const onFocusOut = () => {
		// Focus moving between two fields passes through here; only a real blur ends it.
		setTimeout(() => {
			if (textFieldFocused()) return;
			clearTimeout(appearTimer);
			sawKeyboard = false;
			setOpen(false);
		}, 0);
	};

	const onResize = () => {
		if (!textFieldFocused()) {
			fullHeight = viewportHeight();
			setOpen(false);
			return;
		}
		if (viewportShrunk()) {
			sawKeyboard = true;
			setOpen(true);
		} else if (sawKeyboard) {
			// Closed (for example by the back gesture) while the field kept focus.
			setOpen(false);
		}
	};

	doc.addEventListener("focusin", onFocusIn);
	doc.addEventListener("focusout", onFocusOut);
	win.addEventListener("resize", onResize);
	viewport?.addEventListener("resize", onResize);
	return () => {
		clearTimeout(appearTimer);
		doc.removeEventListener("focusin", onFocusIn);
		doc.removeEventListener("focusout", onFocusOut);
		win.removeEventListener("resize", onResize);
		viewport?.removeEventListener("resize", onResize);
		setOpen(false);
	};
}
